import _ from 'lodash';

import { USE_24_HOUR } from '../constants';

// Spreadsheet column labels are bijective base-26: 26 is Z and 27 is AA, so a plain base-36
// conversion starts emitting digits past column 26.
export const numToAlpha = (num: number): string => {
  let result = '';
  let remaining = num;

  while (remaining > 0) {
    const remainder = (remaining - 1) % 26;

    result = String.fromCharCode(65 + remainder) + result;
    remaining = Math.floor((remaining - 1) / 26);
  }

  return result;
};

export const toPercent = (value: number) =>
  `${Math.round(value * 100 * 100) / 100}%`;

// Minutes past midnight wrap, so an end time of 1440 reads as 12:00 AM rather than '0:00 PM'.
export const numToTime = (value: number) => {
  let hours = Math.floor(value / 60) % 24;
  let minutes: any = value % 60;
  const suffix = hours >= 12 ? 'PM' : 'AM';

  // Hour 0 is 12 AM and hour 12 is 12 PM — neither has a 12-hour form of '0'.
  if (!USE_24_HOUR) {
    hours = hours % 12 || 12;
  }

  if (minutes < 10) {
    minutes = `0${minutes}`;
  }

  return `${hours}:${minutes}${USE_24_HOUR ? '' : ` ${suffix}`}`;
};

export const timeToNum = (time: string) => {
  let currTime = time;

  const ampm = _.takeRight(currTime, 2).join('');
  currTime = _.trimEnd(currTime, ampm);
  currTime = _.trim(currTime, ' ');

  const hours = currTime.split(':').shift();
  const minutes = currTime.split(':').pop();

  let sum = (Number(hours) % 12) * 60 + Number(minutes);
  if (ampm === 'PM') {
    sum += 12 * 60;
  }

  return sum;
};

export const toListSentence = (list: string[]) => {
  let result = '';

  if (list.length === 2) return `${list[0]} and ${list[1]}`;

  list.forEach((item, index) => {
    result = `${result}${item}`;

    if (index <= list.length - 3) {
      result = `${result}, `;
    } else if (index === list.length - 2) {
      result = `${result}, and `;
    }
  });

  return result;
};

export const toScore = (value: string, multiple?: number) => {
  return (Number(value) * (multiple || 1)).toFixed(1);
}

export const numToTimeResult = (time: number) => {
  const minutes = getMinutes(time);

  if (minutes > 0) {
    return `${minutes}m ${getSeconds(time)}.${getHundredthSeconds(time)}s`;
  } else {
    return `${getSeconds(time)}.${getHundredthSeconds(time)}s`;
  }
}

export const getMinutes = (time: number) => Math.floor(time / 60000) % 60

export const getSeconds = (time: number) => Math.floor(time / 1000) % 60;

export const getHundredthSeconds = (time: number) => formatTime(
  Math.floor(time / 10) % 100
);

export const formatTime = (time: number) => {
  return time < 10 ? `0${time}` : time.toString();
}

export const toWeighted = (weightedScore?: string) => {
  let result = '-';

  if (weightedScore && weightedScore !== '-') {
    result = toScore(weightedScore);
  }

  return result;
}

// Metric <-> imperial unit conversion. Milestones are always stored metric; these
// power the imperial values shown when a user toggles the measurement editor.
const LBS_PER_KG = 2.2046226218;
const CM_PER_INCH = 2.54;
const INCHES_PER_FOOT = 12;

export const kgToLbs = (kg: number) => kg * LBS_PER_KG;
export const lbsToKg = (lbs: number) => lbs / LBS_PER_KG;

export const cmToFeetInches = (cm: number): { feet: number; inches: number } => {
  const totalInches = Math.round(cm / CM_PER_INCH);
  return { feet: Math.floor(totalInches / INCHES_PER_FOOT), inches: totalInches % INCHES_PER_FOOT };
};

export const feetInchesToCm = (feet: number, inches: number) =>
  (feet * INCHES_PER_FOOT + inches) * CM_PER_INCH;


